import React, { useState } from "react";
import { Badge, Row, Col } from "react-bootstrap";
import { CommonButton, CommonModal, CommonSelect } from "../Input";
import DataTable from "../table/DataTable";

// Import images
import bill100 from "../../../images/cash/100_dollar.png";
import bill50 from "../../../images/cash/50_dollar.png";
import bill20 from "../../../images/cash/20_dollar.png";
import bill10 from "../../../images/cash/10_dollar.png";
import bill5 from "../../../images/cash/5_dollar.png";
import bill1 from "../../../images/cash/1_dollar.png";

const CreateRequestModal = ({ show, onHide }) => {
  const [quantities, setQuantities] = useState({});

  const denominations = [
    { id: "bill100", label: "100 Dollars", value: 100, image: bill100 },
    { id: "bill50", label: "50 Dollars", value: 50, image: bill50 },
    { id: "bill20", label: "20 Dollars", value: 20, image: bill20 },
    { id: "bill10", label: "10 Dollars", value: 10, image: bill10 },
    { id: "bill5", label: "5 Dollars", value: 5, image: bill5 },
    { id: "bill1_dollar", label: "1 Dollar", value: 1, image: bill1 },
    { id: "bill1_coin", label: "1 Dollar", value: 1, image: bill1 }, // Reusing dummy coin
    { id: "coin50c", label: "50 Cents", value: 0.5 },
    { id: "coin25c", label: "25 Cents", value: 0.25 },
    { id: "coin10c", label: "10 Cents", value: 0.1 },
    { id: "coin5c", label: "5 Cents", value: 0.05 },
    { id: "coin1c", label: "1 Cent", value: 0.01 },
  ];

  const handleQuantityChange = (id, val) => {
    const value = Math.max(0, parseInt(val) || 0);
    setQuantities({ ...quantities, [id]: value });
  };

  const totalAmount = denominations.reduce((sum, item) => {
    return sum + item.value * (quantities[item.id] || 0);
  }, 0);

  const columns = [
    {
      label: "IMAGE",
      key: "image",
      className: "ps-4 text-center",
      cellClassName: "ps-4 text-center",
      style: { width: "100px" },
      render: (item) => (
        <div
          className="d-flex align-items-center justify-content-center border rounded bg-white overflow-hidden mx-auto"
          style={{ width: "45px", height: "30px", borderColor: "#eee" }}
        >
          {item.image ? (
            <img src={item.image} alt={item.label} style={{ width: "100%", height: "auto" }} />
          ) : (
            <div className="text-muted fs-8 fw-bold">$</div>
          )}
        </div>
      ),
    },
    {
      label: "DENOMINATION",
      key: "label",
      style: { width: "250px" },
      render: (item) => <span className="fs-13 fw-bold text-black">{item.label}</span>,
    },
    {
      label: "INVENTORY",
      key: "inventory",
      className: "text-center",
      cellClassName: "text-center",
      style: { width: "150px" },
      render: () => <span className="text-muted fs-11">--</span>,
    },
    {
      label: "QUANTITY",
      key: "quantity",
      className: "text-center",
      cellClassName: "text-center",
      style: { width: "200px" },
      render: (item) => (
        <div className="d-flex justify-content-center">
          <input
            type="number"
            min="0"
            className="form-control text-center py-1"
            style={{
              height: "36px",
              width: "100px",
              fontSize: "13px",
              borderColor: "#CBD5E1",
              borderRadius: "8px",
            }}
            value={quantities[item.id] || ""}
            placeholder="0"
            onChange={(e) => handleQuantityChange(item.id, e.target.value)}
          />
        </div>
      ),
    },
    {
      label: "TOTAL",
      key: "total",
      className: "pe-4 text-end",
      cellClassName: "pe-4 text-end",
      style: { width: "180px" },
      render: (item) => (
        <div className="fw-bold fs-13 text-black">
          USD {(item.value * (quantities[item.id] || 0)).toFixed(2)}
        </div>
      ),
    },
  ];

  return (
    <CommonModal
      show={show}
      onHide={onHide}
      title="Create Request"
      fullscreen={true}
      headerClassName="px-4 py-3"
      footer={
        <div className="d-flex justify-content-end gap-3 w-100 px-3">
          <CommonButton variant="outline" onClick={onHide}>
            Cancel
          </CommonButton>
          <CommonButton className="px-5" onClick={onHide}>
            Confirm
          </CommonButton>
        </div>
      }
    >
      <div className="px-4 pt-3">
        <Row className="g-4 mb-4">
          <Col md={6}>
            <label className="text-muted fs-11 mb-2 d-block">Type</label>
            <CommonSelect
              options={[{ label: "Replenishment (Vault to Cash)", value: "1" }]}
              placeholder="Replenishment (Vault to Cash)"
            />
          </Col>
          <Col md={6}>
            <label className="text-muted fs-11 mb-2 d-block">Currency</label>
            <CommonSelect
              options={[{ label: "US Dollar", value: "USD" }]}
              placeholder="US Dollar"
            />
          </Col>
        </Row>

        <div className="d-flex justify-content-end gap-2 mb-3">
          <Badge
            bg="light"
            className="text-black border px-3 py-2 d-flex align-items-center"
            style={{
              borderRadius: "20px",
              fontSize: "12px",
              backgroundColor: "#F1F5F9",
              borderColor: "#E2E8F0",
              height: "40px",
            }}
          >
            <span className="text-muted me-2">Total cash:</span>
            <span className="fw-bold" style={{ color: "#1E40AF" }}>
              USD 0
            </span>
          </Badge>
          <Badge
            bg="light"
            className="text-black border px-3 py-2 d-flex align-items-center"
            style={{
              borderRadius: "20px",
              fontSize: "12px",
              backgroundColor: "#F1F5F9",
              borderColor: "#E2E8F0",
              height: "40px",
            }}
          >
            <span className="text-muted me-2">Request Total:</span>
            <span className="fw-bold" style={{ color: "#1E40AF" }}>
              USD {totalAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
            </span>
          </Badge>
        </div>

        <div className="card shadow-none rounded-4 overflow-hidden border border-light">
          <DataTable
            columns={columns}
            data={denominations}
            showPagination={false}
            minWidth="100%"
          />
        </div>
      </div>
    </CommonModal>
  );
};

export default CreateRequestModal;
